import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/useToast';
import { googleAdsAccountAtom } from '@/store';
import { negativeKeywordListsQueryOptions } from '@/lib/query-options';
import {
  getCampaignNegativeKeywordLists,
  applyNegativeKeywordListToCampaigns,
  removeNegativeKeywordListFromCampaign,
} from '@/lib/apis';

interface ApplyListsToCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaignId: number;
}

/**
 * Campaign-side counterpart to ApplyListToCampaignsModal: pick which account shared negative
 * lists apply to THIS campaign. Lists already applied are pre-checked; unchecking detaches.
 */
export function ApplyListsToCampaignModal({ isOpen, onClose, campaignId }: ApplyListsToCampaignModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const accountId = googleAdsAccount?.googleads_account_id;

  const { data: listsData, isLoading: isLoadingLists } = useQuery({
    ...negativeKeywordListsQueryOptions(accountId),
    enabled: !!accountId && isOpen,
  });
  const lists = useMemo(() => listsData?.lists ?? [], [listsData]);

  const { data: appliedData } = useQuery({
    queryKey: ['campaign', campaignId, 'negative-keyword-lists'],
    queryFn: () => getCampaignNegativeKeywordLists(campaignId),
    enabled: isOpen,
  });
  const appliedIds = useMemo(
    () => new Set(appliedData?.applied_shared_set_ids ?? []),
    [appliedData],
  );

  // Desired checked set, seeded from the applied lists each time the modal opens / data loads.
  const [checked, setChecked] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (isOpen) setChecked(new Set(appliedData?.applied_shared_set_ids ?? []));
  }, [isOpen, appliedData]);

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toApply = useMemo(() => [...checked].filter((id) => !appliedIds.has(id)), [checked, appliedIds]);
  const toDetach = useMemo(() => [...appliedIds].filter((id) => !checked.has(id)), [checked, appliedIds]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      for (const listId of toApply) {
        await applyNegativeKeywordListToCampaigns(listId, [campaignId]);
      }
      for (const listId of toDetach) {
        await removeNegativeKeywordListFromCampaign(listId, campaignId);
      }
    },
    onSuccess: () => {
      // Refresh the campaign negatives view (inherited rows), the applied-lists set, and list counts.
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'negative-keywords'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaignId, 'negative-keyword-lists'] });
      queryClient.invalidateQueries({ queryKey: ['negative-keyword-lists'] });
      onClose();
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update lists', description: error.message, variant: 'destructive' });
    },
  });

  const hasChanges = toApply.length > 0 || toDetach.length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-900">Apply Negative Keyword Lists</DialogTitle>
          <DialogDescription>
            Select the account-level lists to apply to this campaign. Their keywords are excluded across the campaign.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {isLoadingLists ? (
            <div className="text-center py-8 text-gray-500">Loading lists...</div>
          ) : lists.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Ban className="h-10 w-10 text-gray-500 mb-3" strokeWidth={1} />
              <p className="text-gray-500">No negative keyword lists exist yet. Create one on the Assets page.</p>
            </div>
          ) : (
            <div className="rounded-lg border bg-white divide-y divide-gray-200">
              {lists.map((list) => {
                const isChecked = checked.has(list.id);
                const wasApplied = appliedIds.has(list.id);
                return (
                  <div
                    key={list.id}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 ${isChecked ? 'bg-[#290BD9]/5' : ''}`}
                    onClick={() => toggle(list.id)}
                  >
                    <Checkbox checked={isChecked} onCheckedChange={() => toggle(list.id)} onClick={(e) => e.stopPropagation()} />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900">{list.name}</span>
                      <span className="text-xs text-gray-500 ml-2">
                        {list.member_count} keyword{list.member_count === 1 ? '' : 's'}
                      </span>
                    </div>
                    {wasApplied && (
                      <Badge variant="outline" className="bg-gray-100 text-gray-600">Applied</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <SaveChangesButton
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges}
            saving={saveMutation.isPending}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
